import {registerMaterialGraphNode} from "#material/register"
import type {BlendNormalsSockets} from "#material/nodes/vector/blend-normals/definition"
import {blendNormalsDefinition} from "#material/nodes/vector/blend-normals/definition"
import {MaterialNode} from "#material/nodes/base/material/material-node"
import {threeConvert, threeRGBColor} from "#material/three/three-utils"
import {threeBlendNormals} from "#material/nodes/vector/blend-normals/three-node"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {ThreeReturns} from "#material/sockets/three"
import type {CombineRgbNode, SeparateXyzNode, VectorMathNode} from "#material/cycles/types"
import {cyclesColor} from "#material/cycles/helpers"

const vectorMath = (
    mathType: VectorMathNode["parameters"]["math_type"],
    inputs: Partial<VectorMathNode["inputs"]>,
    parameters: Partial<Omit<VectorMathNode["parameters"], "math_type">> = {},
): VectorMathNode => ({
    type: "vector_math",
    inputs: {vector1: undefined, vector2: undefined, vector3: undefined, scale: undefined, ...inputs},
    parameters: {math_type: mathType, vector1: undefined, vector2: undefined, vector3: undefined, scale: undefined, ...parameters},
    resources: {},
})

@registerMaterialGraphNode
export class BlendNormals extends MaterialNode<BlendNormalsSockets> {
    public override get definition() {
        return blendNormalsDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<BlendNormalsSockets>> => {
        const {color1, color2} = await evaluator.getAll({
            color1: this.parameters.inputs.color1,
            color2: this.parameters.inputs.color2,
        })
        const color1Value = color1?.toColor() ?? threeConvert(this.parameters.settings.color1, threeRGBColor) ?? threeRGBColor({r: 0, g: 0, b: 0})

        const color2Value = color2?.toColor() ?? threeConvert(this.parameters.settings.color2, threeRGBColor) ?? threeRGBColor({r: 0, g: 0, b: 0})

        return {color: threeBlendNormals(color1Value, color2Value)}
    }

    // Reoriented normal mapping, the same construction threeBlendNormals evaluates in one expression.
    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<BlendNormalsSockets>> => {
        const {color1, color2} = await evaluator.getAll({
            color1: this.parameters.inputs.color1,
            color2: this.parameters.inputs.color2,
        })

        const t = vectorMath(
            "multiply_add",
            {vector1: color1},
            {vector1: cyclesColor(this.parameters.settings.color1), vector2: [2, 2, 2], vector3: [-1, -1, 0]},
        )
        const u = vectorMath(
            "multiply_add",
            {vector1: color2},
            {vector1: cyclesColor(this.parameters.settings.color2), vector2: [-2, -2, 2], vector3: [1, 1, -1]},
        )

        const alignment = vectorMath("dot_product", {vector1: [t, "vector"], vector2: [u, "vector"]})
        const scaledT = vectorMath("scale", {vector1: [t, "vector"], scale: [alignment, "value"]})

        const tz: SeparateXyzNode = {type: "separate_xyz", inputs: {vector: [t, "vector"]}, parameters: {vector: undefined}, resources: {}}
        const scaledU = vectorMath("scale", {vector1: [u, "vector"], scale: [tz, "z"]})

        const difference = vectorMath("subtract", {vector1: [scaledT, "vector"], vector2: [scaledU, "vector"]})
        const shifted = vectorMath("add", {vector1: [difference, "vector"]}, {vector2: [1, 1, 1]})

        const blended = vectorMath("scale", {vector1: [shifted, "vector"]}, {scale: 0.5})

        const components: SeparateXyzNode = {type: "separate_xyz", inputs: {vector: [blended, "vector"]}, parameters: {vector: undefined}, resources: {}}
        const encoded: CombineRgbNode = {
            type: "combine_rgb",
            inputs: {r: [components, "x"], g: [components, "y"], b: [components, "z"]},
            parameters: {r: undefined, g: undefined, b: undefined},
            resources: {},
        }

        return {color: [encoded, "image"]}
    }
}
